﻿<template>
  <IonModal
    :is-open="isOpen"
    :backdrop-dismiss="backdropDismiss"
    :show-backdrop="showBackdrop"
    @didDismiss="handleDismiss"
    @willDismiss="handleWillDismiss"
    class="custom-modal"
    :style="{
      '--max-width': maxWidth,
    }"
  >
    <div class="modal-content">
      <!-- Header -->
      <div v-if="showHeader" class="modal-header">
        <div class="modal-header-text">
          <h2 v-if="title" class="modal-title">{{ title }}</h2>
          <p v-if="subtitle" class="modal-subtitle">{{ subtitle }}</p>
        </div>
        <button v-if="showCloseButton" @click="handleClose" class="modal-close-button">
          <IonIcon name="close-outline" />
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <slot />
      </div>

      <!-- Footer -->
      <div v-if="showFooter" class="modal-footer">
        <Button
          v-if="showCancelButton"
          :type="cancelButtonType"
          @click="handleCancel"
          :disabled="cancelButtonDisabled"
          class="modal-button modal-button-cancel"
        >
          {{ cancelText }}
        </Button>
        <Button
          v-if="showConfirmButton"
          :type="confirmButtonType"
          @click="handleConfirm"
          :disabled="confirmButtonDisabled"
          class="modal-button modal-button-confirm"
        >
          {{ confirmText }}
        </Button>
      </div>
    </div>
  </IonModal>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { IonModal, IonIcon } from '@ionic/vue';
import Button from '@/common/components/ui/button/Button.vue';
import { type ButtonType } from '@/common/constants';

interface Props {
  // Modal state
  isOpen?: boolean;

  // Modal configuration
  initialBreakpoint?: number;
  breakpoints?: number[];
  backdropDismiss?: boolean;
  showBackdrop?: boolean;

  // Header configuration
  showHeader?: boolean;
  title?: string;
  subtitle?: string;
  showCloseButton?: boolean;

  // Footer configuration
  showFooter?: boolean;
  showCancelButton?: boolean;
  cancelButtonType?: ButtonType;
  showConfirmButton?: boolean;
  confirmButtonType?: ButtonType;
  cancelText?: string;
  confirmText?: string;
  confirmButtonDisabled?: boolean;
  cancelButtonDisabled?: boolean;
  width?: string;
  maxWidth?: string;
  height?: string;
  maxHeight?: string;
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void;
  (e: 'confirm'): void;
  (e: 'cancel'): void;
  (e: 'close'): void;
  (e: 'dismiss', event: CustomEvent): void;
  (e: 'willDismiss', event: CustomEvent): void;
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  initialBreakpoint: 1,
  breakpoints: () => [0, 1],
  backdropDismiss: true,
  showBackdrop: true,
  showHeader: true,
  showCloseButton: true,
  showFooter: false,
  showCancelButton: true,
  cancelButtonType: undefined,
  showConfirmButton: true,
  confirmButtonType: 'primary',
  cancelText: 'Annuler',
  confirmText: 'Confirmer',
  confirmButtonDisabled: false,
  cancelButtonDisabled: false,
  maxWidth: '600px',
});

const emit = defineEmits<Emits>();

// Handle modal dismiss
const handleDismiss = (event: CustomEvent) => {
  emit('update:isOpen', false);
  emit('dismiss', event);
};

// Handle modal will dismiss
const handleWillDismiss = (event: CustomEvent) => {
  emit('willDismiss', event);
};

// Handle close button
const handleClose = () => {
  emit('update:isOpen', false);
  emit('close');
};

// Handle cancel button
const handleCancel = () => {
  emit('update:isOpen', false);
  emit('cancel');
};

// Handle confirm button
const handleConfirm = () => {
  emit('confirm');
};

// Watch isOpen prop to sync with internal state
watch(
  () => props.isOpen,
  (newValue) => {
    if (!newValue) {
      emit('update:isOpen', false);
    }
  }
);
</script>

<style scoped>
/* Modal Container - unified for all platforms */
.custom-modal {
  --backdrop-opacity: 0.5;
  --width: 90%;
  --max-width: 800px;
  --height: auto;
  --max-height: 85vh;
  --border-radius: 24px;
  --box-shadow: var(--ion-box-shadow-hover);
  --background: var(--ion-card-background, #ffffff);
}

.custom-modal::part(content) {
  border-radius: 24px;
  background: var(--ion-card-background, #ffffff);
}

.custom-modal::part(backdrop) {
  --backdrop-opacity: 0.5;
}

/* Modal Content Container */
.modal-content {
  background: var(--ion-card-background, #ffffff);
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  position: relative;

  /* Subtle border for definition */
  border: 1px solid var(--ion-border-color-light, rgba(var(--ion-text-color-rgb, 0, 0, 0), 0.1));

  /* Glass-like effect */
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid
    var(--ion-border-color-light, rgba(var(--ion-text-color-rgb, 0, 0, 0), 0.1));
  background: var(--ion-card-background, #ffffff);
}

.modal-header-text {
  flex: 1;
  padding-right: 1rem;
}

.modal-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--ion-text-color);
  line-height: 1.3;
}

.modal-subtitle {
  margin: 0.5rem 0 0 0;
  font-size: 0.95rem;
  color: var(--ion-text-color-secondary);
  line-height: 1.4;
}

.modal-close-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ion-text-color-secondary);
  font-size: 1.5rem;
  transition: color 0.2s ease;
  flex-shrink: 0;
}

.modal-close-button:hover {
  color: var(--ion-text-color);
}

/* Modal Body */
.modal-body {
  display: flex;
  flex-direction: column;
  padding: 2rem;
  background: var(--ion-card-background, #ffffff);
  overflow-y: auto;
  flex: 1;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid var(--ion-border-color-light, rgba(var(--ion-text-color-rgb, 0, 0, 0), 0.1));
  background: var(--ion-card-background, #ffffff);
}

/* Mobile - ensure consistent background */
@media (max-width: 768px) {
  .custom-modal {
    --width: 95%;
    --background: var(--ion-card-background, #ffffff);
  }

  .custom-modal::part(content) {
    background: var(--ion-card-background, #ffffff) !important;
  }

  .modal-content {
    background: var(--ion-card-background, #ffffff) !important;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    background: var(--ion-card-background, #ffffff) !important;
  }

  .modal-header {
    padding: 1rem 1.5rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .modal-footer {
    padding: 1rem 1.5rem;
  }

  .modal-title {
    font-size: 1.25rem;
  }
}
</style>
